import { Theme } from "@mui/material/styles";
import { colors } from "./palette";
import typography from "./typography";
import sizeOverrides from "./sizeOverrides";

const components = {
  MuiCssBaseline: {
    styleOverrides: {
      "ul, ol": {
        listStyle: "none",
        padding: 0,
      },
      "::-webkit-scrollbar": {
        width: "4px",
        height: "4px",
      },
      "::-webkit-scrollbar-track": {
        backgroundColor: "#f1f1f1",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
      },
      "::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#555",
      },
    },
  },

  MuiTypography: {
    defaultProps: {
      variantMapping: {
        h1ExtraBold: "h1",
        h2ExtraBold: "h2",
        h3ExtraBold: "h3",
        h3Bold: "h3",
        h4ExtraBold: "h4",
        h5Bold: "h5",
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        borderRadius: "50px",
        boxShadow: "none",
        WhiteSpace: "nowrap",
        columnGap: "4px",
        textTransform: "none",
        "& .MuiButton-endIcon, & .MuiButton-startIcon": {
          margin: 0,
        },
        // Automatically rotate icons in RTL mode for Arabic
        ...(theme.direction === "rtl" && {
          "& .MuiButton-endIcon": {
            transform: "scaleX(-1)",
          },
          "& .MuiButton-startIcon": {
            transform: "scaleX(-1)",
          },
        }),
      }),
    },
    variants: [
      {
        props: { variant: "primary-sm" },
        style: {
          ...sizeOverrides.sm,
          backgroundColor: colors.Green100,
          color: colors.White,
          padding: "8px 14px",
          fontSize: "14px",

          "&:hover": {
            backgroundColor: colors.Green200,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
        },
      },
      {
        props: { variant: "primary-md" },
        style: {
          ...sizeOverrides.md,
          backgroundColor: colors.Green100,
          color: colors.White,
          padding: "10px 16px",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: colors.Green200,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
        },
      },
      {
        props: { variant: "primary-lg" },
        style: {
          ...sizeOverrides.lg,
          backgroundColor: colors.Green100,
          color: colors.White,
          padding: "10px 18px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: colors.Green200,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
        },
      },
      {
        props: { variant: "primary-xl" },
        style: {
          ...sizeOverrides.xl,
          backgroundColor: colors.Green100,
          color: colors.White,
          padding: "12px 20px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: colors.Green200,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
        },
      },

      // Outlined Button Variants
      {
        props: { variant: "outlined-sm" },
        style: {
          ...sizeOverrides.sm,
          backgroundColor: "transparent",
          border: `1px solid ${colors.Green100}`,
          color: colors.Green100,
          padding: "8px 14px",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: colors.Green25,
            borderColor: colors.Green200,
            color: colors.Green200,
          },
          "&:disabled": {
            borderColor: colors.Green25,
            color: colors.Green25,
          },
        },
      },
      {
        props: { variant: "outlined-md" },
        style: {
          ...sizeOverrides.md,
          backgroundColor: "transparent",
          border: `1px solid ${colors.Green100}`,
          color: colors.Green100,
          padding: "10px 16px",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: colors.Green25,
            borderColor: colors.Green200,
            color: colors.Green200,
          },
          "&:disabled": {
            borderColor: colors.Green25,
            color: colors.Green25,
          },
        },
      },
      {
        props: { variant: "outlined-lg" },
        style: {
          ...sizeOverrides.lg,
          backgroundColor: "transparent",
          border: `1px solid ${colors.Green100}`,
          color: colors.Green100,
          padding: "10px 18px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: colors.Green25,
            borderColor: colors.Green200,
            color: colors.Green200,
          },
          "&:disabled": {
            borderColor: colors.Green25,
            color: colors.Green25,
          },
        },
      },
      {
        props: { variant: "outlined-xl" },
        style: {
          ...sizeOverrides.xl,
          backgroundColor: "transparent",
          border: `1px solid ${colors.Green100}`,
          color: colors.Green100,
          padding: "12px 20px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: colors.Green25,
            borderColor: colors.Green200,
            color: colors.Green200,
          },
          "&:disabled": {
            borderColor: colors.Green25,
            color: colors.Green25,
          },
        },
      },

      // Text Button Variants
      {
        props: { variant: "text-sm" },
        style: {
          ...sizeOverrides.sm,
          backgroundColor: "transparent",
          color: colors.Green100,
          padding: "8px 14px",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: colors.Green25,
            color: colors.Green100,
          },
          "&:disabled": {
            color: colors.Green25,
          },
        },
      },
      {
        props: { variant: "text-md" },
        style: {
          ...sizeOverrides.md,
          backgroundColor: "transparent",
          color: colors.Green100,
          padding: "10px 16px",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: colors.Green25,
            color: colors.Green100,
          },
          "&:disabled": {
            color: colors.Green25,
          },
        },
      },
      {
        props: { variant: "text-lg" },
        style: {
          ...sizeOverrides.lg,
          backgroundColor: "transparent",
          color: colors.Green100,
          padding: "10px 18px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: colors.Green25,
            color: colors.Green100,
          },
          "&:disabled": {
            color: colors.Green25,
          },
        },
      },
      {
        props: { variant: "text-xl" },
        style: {
          ...sizeOverrides.xl,
          backgroundColor: "transparent",
          color: colors.Green100,
          padding: "12px 20px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: colors.Green25,
            color: colors.Green100,
          },
          "&:disabled": {
            color: colors.Green25,
          },
        },
      },

      // Secondary Button Variants
      {
        props: { variant: "secondary-sm" },
        style: {
          ...sizeOverrides.sm,
          backgroundColor: colors.Green25,
          color: colors.Green100,
          padding: "8px 14px",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: "transparent",
            border: `1px solid ${colors.Green50}`,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
          "&:disabled .MuiButton-icon .MuiSvgIcon-root": {
            color: colors.White,
            fill: colors.White,
          },
          "&:disabled .MuiButton-icon .MuiSvgIcon-root path": {
            color: colors.White,
            fill: colors.White,
            stroke: colors.White,
          },
        },
      },
      {
        props: { variant: "secondary-md" },
        style: {
          ...sizeOverrides.md,
          backgroundColor: colors.Green25,
          color: colors.Green100,
          padding: "10px 16px",
          fontSize: "14px",
          "&:hover": {
            backgroundColor: "transparent",
            border: `1px solid ${colors.Green50}`,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
        },
      },
      {
        props: { variant: "secondary-lg" },
        style: {
          ...sizeOverrides.lg,
          backgroundColor: colors.Green25,
          color: colors.Green100,
          padding: "10px 18px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: "transparent",
            border: `1px solid ${colors.Green50}`,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
        },
      },
      {
        props: { variant: "secondary-xl" },
        style: {
          ...sizeOverrides.xl,
          backgroundColor: colors.Green25,
          color: colors.Green100,
          padding: "12px 20px",
          fontSize: "16px",
          "&:hover": {
            backgroundColor: "transparent",
            border: `1px solid ${colors.Green50}`,
          },
          "&:disabled": {
            backgroundColor: colors.Green25,
            color: colors.White,
          },
        },
      },
    ],
    defaultProps: {
      disableElevation: true,
      disableFocusRipple: true,
      disableRipple: true,
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        // Automatically rotate icons in RTL mode for Arabic
        ...(theme.direction === "rtl" && {
          "& svg": {
            transform: "scaleX(-1)",
          },
          "&.MuiPickersArrowSwitcher-button svg": {
            /* Pickers already swap the arrow icons for RTL, so undo the global flip here */
            transform: "none",
          },
        }),
      }),
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: "8px",
        border: `1px solid ${colors.Gray200}`,
        padding: "0 16px",

        "&:has(.MuiSelect-select)": {
          borderRadius: "50px",
        },
        "& .MuiSelect-select": {
          ...typography.body14Regular,
          padding: "10px 32px 10px 0",
        },
        "& .MuiSelect-select::placeholder": {
          color: colors.Gray400,
        },
        "&.Mui-error": {
          borderColor: `${colors.red100} !important`,
        },
        "&:has(.MuiSelect-select) .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: {
        color: colors.Black100,
        ...typography.body14Regular,

        "& .MuiInputBase-input": {
          padding: "8px 0",
        },
        "& .MuiInputBase-input::placeholder": {
          color: colors.Gray400,
        },
        "& .MuiFormHelperText-root ": {
          ...typography.body14Regular,
          color: colors.dark600,
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "33px",
          border: "1px solid var(--Gray---200, #CFCFCF)",
          background: "var(--Base-White, #FFF)",
          padding: "0 12px !important",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        },
        "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
          borderRadius: "8px",
        },
        "& .Mui-focused": {
          borderColor: `${colors.Green50} !important`,
        },
        "& .Mui-disabled": {
          backgroundColor: `${colors.Gray50} !important`,
        },
        "& .Mui-error": {
          borderColor: `${colors.red100} !important`,
        },
      },
    },
    variants: [],
  },

  MuiAutocomplete: {
    styleOverrides: {
      root: {
        width: "100%",
        "& .MuiInputBase-root": {
          minHeight: "40px",
          padding: "0 12px !important",
          "& .MuiAutocomplete-input": {
            padding: "0 !important",
            minHeight: "40px",
          },
          "& .MuiAutocomplete-endAdornment": {
            right: "8px !important",
          },
        },
        "& .MuiChip-root": {
          height: "24px",
          borderRadius: "6px",
          border: "1px solid var(--Gray-300, #D0D5DD)",
          background: "var(--Base-White, #FFF)",
          gap: "4px",
          margin: "2px",
          padding: "2px 5px",
          "& .MuiChip-label": {
            padding: 0,
          },
          "& .MuiChip-deleteIcon": {
            margin: 0,
          },
        },
        "& .MuiAutocomplete-tag": {
          margin: "2px",
        },
      },
      listbox: {
        "& .MuiAutocomplete-option": {
          minHeight: "36px",
          padding: "8px 12px",
        },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: "unset",
        background: "var(--Base-White, #FFF)",
        minHeight: "41px",
        "& .MuiSelect-select": {
          paddingRight: "30px !important",
          paddingY: "0px !important",
          fontFamily: "Montserrat",
          color: "var(--Gray-900, #101828)",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "24px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "& .MuiOutlinedInput-root": {
          border: "1px solid var(--Gray---200, #CFCFCF)",
          "&.Mui-error": {
            borderColor: `${colors.red100} !important`,
          },
        },
      },
    },
  },

  MuiFormControl: {
    styleOverrides: {
      root: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      },
    },
  },

  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontFamily: "Montserrat",
        color: "var(--Dark---1000, #111)",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "20px",
      },
    },
  },

  MuiPopper: {
    styleOverrides: {
      root: {
        width: "fit-content !important",
      },
    },
  },

  MuiSlider: {
    styleOverrides: {
      root: {
        height: 8,
      },
      thumb: {
        width: 24,
        height: 24,
        backgroundColor: "var(--Gray---200, #CFCFCF)",
        "&::before": {
          content: '""',
          width: 12,
          height: 12,
          borderRadius: 12,
          backgroundColor: "var(--Brand-600, #008B07)",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        },
      },
      track: {
        height: 8,
        borderRadius: 4,
        backgroundColor: "var(--Brand-600, #008B07)",
      },
      rail: {
        height: 8,
        borderRadius: 4,
        backgroundColor: "var(--Gray---200, #CFCFCF)",
      },
    },
  },

  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        color: "var(--Dark---800, #414141)",
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "24px",
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      // Base container of the switch
      root: {
        width: 44, // Total width of the switch component
        height: 26, // Total height of the switch component
        padding: 0, // Removes any padding around the switch
      },

      // The moving part of the switch that contains the thumb
      switchBase: {
        padding: 0, // Removes padding around the moving part
        margin: 2, // Small margin for positioning
        transitionDuration: "300ms", // Animation duration when switching states

        // Styles when the switch is checked (ON state)
        "&.Mui-checked": {
          transform: "translateX(18px)", // Moves the thumb to the right
          color: "#fff", // Color of the thumb when checked

          // Track styles when checked
          "& + .MuiSwitch-track": {
            backgroundColor: "var(--Brand-600, #008B07)", // Green background when ON
            opacity: 1,
            border: 0,
          },

          // Track styles when checked but disabled
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.5,
          },
        },

        // Styles when the switch has keyboard focus
        "&.Mui-focusVisible .MuiSwitch-thumb": {
          color: "#33cf4d", // Thumb color when focused
          border: "6px solid #fff", // White border around thumb when focused
        },

        // Styles for disabled state
        "&.Mui-disabled .MuiSwitch-thumb": {
          color: "#f5f5f5", // Light gray thumb when disabled
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.7, // Reduced opacity for disabled track
        },
      },

      // The circular button (thumb) that moves
      thumb: {
        boxSizing: "border-box",
        width: 22, // Width of the circular thumb
        height: 22, // Height of the circular thumb
      },

      // The background track the thumb slides on
      track: {
        borderRadius: 13, // Rounded corners (half of height)
        backgroundColor: "var(--Gray-100, #F2F4F7)", // Light gray background when OFF
        opacity: 1,
        transition: "background-color 500ms", // Smooth color transition
      },
    },
  },
};

export default components;
